import { apiAdn } from "api/common";
import { useEffect, useRef, useState } from "react";
import { useEvent } from "./useEvent";
import { URL_ADN } from "customGlobal";
import { useDispatch, useSelector } from "react-redux";
import {
  decrementUnreadCount,
  incrementUnreadCount,
  setUnreadCountByGroup,
} from "actions/notifications.action";
import {
  getNotifications,
  getUnreadNotificationCountByGroup,
  validateAllNotification,
  validateNotification,
} from "api/notification";
import { ReducerState } from "reducers";
import {
  Notification,
  NotificationGroup,
  NotificationPriority,
  UnreadCountByGroup,
} from "types/Notification";
import toaster from "composants/toaster/toaster";

const PAGE_SIZE = 20;
const NOTIFICATION_OVERLAY_LIMIT = 2;
export const NOTIFICATION_PRIORITY_VALUE: Record<NotificationPriority, number> = {
  TEMPORARY: 0,
  LOW: 1,
  NORMAL: 2,
  HIGH: 3,
  CRITICAL: 4,
};

function vibration(setVibrate: (val: boolean) => void) {
  setVibrate(true);
  return setTimeout(() => {
    setVibrate(false);
  }, 300);
}

export default function useNotifications(tabsState: NotificationGroup) {
  const [tokenEventSource, setTokenEventSource] = useState<string | null>(null);
  const [vibrate, setVibrate] = useState(false);
  const [notifs, setNotifs] = useState<Notification[]>([]);
  const [first, setFirst] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const isLoadingMoreRef = useRef(false);
  const changeTabRef = useRef(0);

  const unreadCountByGroup = useSelector(
    (state: ReducerState) => state.notifications.unreadCountByGroup,
  );

  const nbUnreadNotifications = Object.values(unreadCountByGroup).reduce(
    (sum, n) => sum + (n ?? 0),
    0,
  );
  const hasWorkUnreadNotification = (unreadCountByGroup[NotificationGroup.METIER] ?? 0) > 0;
  const dispatch = useDispatch();

  // Ref miroir de tabsState pour les callbacks asynchrones (markAsRead, SSE).
  // Un setState déclenche un re-render mais la closure d'une promesse en vol
  // capture l'ancienne valeur. La ref donne toujours la valeur courante sans
  // avoir à la mettre en dépendance de chaque useEvent/useCallback.
  const currentTabRef = useRef(tabsState);
  useEffect(() => {
    currentTabRef.current = tabsState;
  }, [tabsState]);

  // ==== GESTION SERVEUR SIDE EVENT (récupération des notifications envoyées par le serveur au front) ====

  // Récupération token SSE
  useEffect(() => {
    if (tokenEventSource) return;
    apiAdn.post("/notifications/stream/token").then((res) => {
      setTokenEventSource(res.data);
    });
  }, [tokenEventSource]);

  // Récupération d'une nouvelle notification via SSE
  const onNotification = useEvent((event: MessageEvent) => {
    const newNotification: Notification = JSON.parse(event.data);
    if (!newNotification.id) return;
    const alreadyExists = notifs.some((notif) => notif.id === newNotification.id);

    if (alreadyExists) return;

    if (newNotification.validatedBy == null) {
      dispatch(incrementUnreadCount(newNotification.group));
      const value = NOTIFICATION_PRIORITY_VALUE[newNotification.priority];
      if (value >= NOTIFICATION_OVERLAY_LIMIT) {
        toaster.notify(newNotification);
      }
    }

    if (newNotification.group === tabsState && newNotification.validatedAt == null) {
      setNotifs((prev) => [newNotification, ...prev]);
    }
  });

  // Erreur sur la communication avec le serveur
  const onError = useEvent(() => {
    setTokenEventSource(null);
  });

  // Ouverture du flux pour récupérer les notifications via SSE
  useEffect(() => {
    if (tokenEventSource == null) return;

    let eventSource: EventSource = new EventSource(
      URL_ADN() + "/notifications/stream?token=" + tokenEventSource,
      { withCredentials: false },
    );

    eventSource.onmessage = onNotification;
    eventSource.onerror = onError;

    return () => {
      if (eventSource) eventSource.close();
    };
  }, [tokenEventSource]);

  // ==== Fin gestion SSE ===

  // Reset de l'état à chaque changement d'onglet. changeTabRef invalide
  // les réponses loadMore en vol pour éviter que des données périmées ne
  // viennent écraser l'onglet courant.
  useEffect(() => {
    changeTabRef.current++;
    isLoadingMoreRef.current = false;
    setIsLoadingMore(false);
    setNotifs([]);
    setFirst(0);
    setHasMore(true);
  }, [tabsState]);

  // TODO: Ce polling peut être optimisé à l'avenir. Le SSE gère déjà les nouvelles notifs
  // en temps réel. On pourrait le remplacer par un fetch on-demand (ouverture du panneau,
  // visibilitychange) pour supprimer la charge réseau constante.
  useEffect(() => {
    function refreshCounts() {
      getUnreadNotificationCountByGroup().then((res) => {
        dispatch(setUnreadCountByGroup(res.data));
      });
    }

    refreshCounts();
    const interval = setInterval(refreshCounts, 30 * 1000);
    return () => clearInterval(interval);
  }, [dispatch]);

  // Tant qu'il y a des notifications non lues, on fait vibrer la cloche toutes les 30s
  useEffect(() => {
    if (!hasWorkUnreadNotification) return;

    vibration(setVibrate);

    const interval = setInterval(() => {
      vibration(setVibrate);
    }, 30 * 1000);

    return () => {
      clearInterval(interval);
      setVibrate(false);
    };
  }, [hasWorkUnreadNotification]);

  // useEvent donne une référence stable (jamais recréée) qui lit toujours les
  // valeurs courantes via une ref interne. Sans ça, chaque changement de state
  // recrée la fonction, ce qui relance le ResizeObserver dans MenuCards et
  // provoque des appels dupliqués.
  // isLoadingMoreRef est le garde synchrone : setIsLoadingMore(true) est async
  // (re-render différé), la ref bloque immédiatement les appels concurrents.
  const loadMore = useEvent(() => {
    if (isLoadingMoreRef.current || !hasMore) return;
    isLoadingMoreRef.current = true;
    setIsLoadingMore(true);
    const gen = changeTabRef.current;

    getNotifications(tabsState, first, PAGE_SIZE)
      .then((res) => {
        if (gen !== changeTabRef.current) return;
        setNotifs((prev) => {
          const ids = new Set(prev.map((n) => n.id));
          return [...prev, ...res.data.filter((n) => !ids.has(n.id))];
        });
        setHasMore(res.data.length === PAGE_SIZE);
        setFirst((prev) => prev + PAGE_SIZE);
      })
      .finally(() => {
        if (gen === changeTabRef.current) {
          isLoadingMoreRef.current = false;
          setIsLoadingMore(false);
        }
      });
  });

  const markAsRead = useEvent((id: string) => {
    validateNotification(id).then((res) => {
      const notif = res.data;
      dispatch(decrementUnreadCount(notif.group));
      // Mise à jour in-place : la notif reste à sa position mais validatedAt est renseigné.
      // On ne la déplace plus entre deux tableaux — le tri unread/read est géré par le serveur.
      if (notif.group === currentTabRef.current) {
        setNotifs((prev) => prev.map((n) => (n.id === id ? notif : n)));
      }
    });
  });

  function markAllAsRead() {
    validateAllNotification().then((res) => {
      setNotifs(res.data.slice(0, PAGE_SIZE));
      setFirst(PAGE_SIZE);
      setHasMore(res.data.length === PAGE_SIZE);
      dispatch(setUnreadCountByGroup({} as UnreadCountByGroup));
    });
  }

  return {
    notifications: notifs, // Toutes les notifs
    nbUnreadNotifications, // Utilisé pour enabled le bouton 'Valider tout'
    countUnreadByGroup: unreadCountByGroup, // Compteur notifs par groupe
    vibrate, // Boolean pour faire vibrer la cloche ou non, dépend du nombre de notifs métiers
    hasMore, // Boolean permet de savoir si des notifs doivent encore être chargées
    isLoading: isLoadingMore, // Chargement initial ou chargement des notifs à la pagination
    loadMore, // CB charge les notifs de la pagination
    markAllAsRead, // CB Bouton 'Valider tout'
    markAsRead, // CB clic notif
  };
}
